import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, forkJoin, map, of, shareReplay, switchMap, throwError } from 'rxjs';

import { ResourceType } from '@completion/enums';
import {
  AdminCheckSheetDto,
  AdminFormDto,
  AdminFormInfo,
  AdminFormListItemDto,
  AdminFormStatus,
  AdminProjectDto,
  FormConnectionDto,
  FormConnectionRequestDto,
  GetFormsParams,
  PaginatedResponse,
} from '@completion/models';
import { BaseService, Resource } from './base.service';
import { NotificationService } from './notification.service';

export class AdminFormConnectionError extends Error {
  constructor(public readonly failedProjects: { projectId: number; error: any }[]) {
    super();
    this.name = 'AdminFormConnectionError';
  }
}

@Injectable({ providedIn: 'root' })
export class AdminFormService extends BaseService {
  private adminProjects$?: Observable<AdminProjectDto[]>;

  constructor(
    readonly http: HttpClient,
    private readonly notificationService: NotificationService,
  ) {
    super(http);
  }

  @Resource(ResourceType.AdminFormGetAll)
  getAdminForms(params: GetFormsParams = {}): Observable<PaginatedResponse<AdminFormListItemDto>> {
    const query = new URLSearchParams();

    if (params.search) query.set('search', params.search);
    query.set('page', String(params.page ?? 0));
    query.set('size', String(params.size ?? 20));
    if (params.sort) query.set('sort', params.sort);

    return this.http.get<PaginatedResponse<AdminFormListItemDto>>(`${this.baseUrl}/admin/forms?${query.toString()}`);
  }

  @Resource(ResourceType.AdminFormGetById)
  getAdminForm(_guid: string): Observable<AdminFormDto> {
    return this.http.get<AdminFormDto>(`${this.baseUrl}/admin/forms/guid/${_guid}`);
  }

  @Resource(ResourceType.AdminFormGetById, ['formGuid'])
  getAdminFormInfo(formGuid: string): Observable<AdminFormInfo> {
    return forkJoin({
      form: this.getAdminForm(formGuid),
      connections: this.getConnections(formGuid),
      projects: this.getAdminProjects(),
    });
  }

  @Resource(ResourceType.AdminFormCreate)
  createAdminForm(_form: Partial<AdminFormDto>): Observable<AdminFormDto> {
    return this.http.post<AdminFormDto>(`${this.baseUrl}/admin/forms`, _form);
  }

  @Resource(ResourceType.AdminFormUpdate)
  updateAdminForm(_form: AdminFormDto): Observable<AdminFormDto> {
    return this.http.put<AdminFormDto>(`${this.baseUrl}/admin/forms/guid/${_form.guid}`, _form);
  }

  @Resource(ResourceType.AdminFormUpdateStatus)
  updateAdminFormStatus(_guid: string, _status: AdminFormStatus): Observable<AdminFormDto> {
    return this.http.patch<AdminFormDto>(`${this.baseUrl}/admin/forms/guid/${_guid}/status`, { status: _status });
  }

  @Resource(ResourceType.AdminFormDelete)
  deleteAdminForm(_guid: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/admin/forms/guid/${_guid}`);
  }

  @Resource(ResourceType.AdminFormGetConnections)
  getConnections(formGuid: string): Observable<FormConnectionDto[]> {
    return this.http.get<FormConnectionDto[]>(`${this.baseUrl}/admin/forms/guid/${formGuid}/connections`);
  }

  @Resource(ResourceType.AdminFormAddConnection)
  addConnections(_request: FormConnectionRequestDto): Observable<AdminFormDto> {
    const projectIds = (_request.projectId ?? []).filter((projectId) => projectId !== 0);

    if (_request.scope === 'GLOBAL' || !projectIds.length) {
      return this.postConnection({ ..._request, projectId: undefined });
    }

    return forkJoin(
      projectIds.map((projectId) =>
        this.postConnection({ ..._request, projectId }).pipe(
          map((response) => ({ projectId, response, error: undefined })),
          catchError((error) => of({ projectId, response: null, error })),
        ),
      ),
    ).pipe(
      switchMap((results) => {
        const failedObject = results
          .filter((result) => result.response === null)
          .map((result) => ({ projectId: result.projectId, error: result.error }));

        if (failedObject.length > 0) {
          throw new AdminFormConnectionError(failedObject);
        }

        const successfulResponses = results
          .map((result) => result.response)
          .filter((response): response is AdminFormDto => response !== null);
        return of(successfulResponses[successfulResponses.length - 1]);
      }),
    );
  }

  @Resource(ResourceType.AdminFormRemoveConnection)
  removeConnection(_formGuid: string, _connection: FormConnectionDto): Observable<void> {
    const params = { ..._connection };
    if (params.projectId === 0 || params.projectId === null || params.projectId === undefined) {
      delete params.projectId;
    }
    return this.http.delete<void>(`${this.baseUrl}/admin/forms/guid/${_formGuid}/connections`, { params });
  }

  @Resource(ResourceType.AdminProjectGetAll)
  getAdminProjects(): Observable<AdminProjectDto[]> {
    if (!this.adminProjects$) {
      this.adminProjects$ = this.http.get<AdminProjectDto[]>(`${this.baseUrl}/admin/projects`).pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
        catchError((error) => {
          this.invalidateAdminProjects();
          return throwError(() => error);
        }),
      );
    }

    return this.adminProjects$;
  }

  invalidateAdminProjects(): void {
    this.adminProjects$ = undefined;
  }

  @Resource(ResourceType.AdminCheckSheetSearch)
  getAdminCheckSheets(search: string): Observable<PaginatedResponse<AdminCheckSheetDto>> {
    const query = new URLSearchParams();
    query.set('search', search);

    return this.http.get<PaginatedResponse<AdminCheckSheetDto>>(`${this.baseUrl}/admin/check-sheets?${query.toString()}`);
  }

  private postConnection(request: Omit<FormConnectionRequestDto, 'projectId'> & { projectId?: number }): Observable<AdminFormDto> {
    const body = { ...request };
    if (body.projectId === 0 || body.projectId === null || body.projectId === undefined) {
      delete body.projectId;
    }
    return this.http.post<AdminFormDto>(`${this.baseUrl}/admin/forms/guid/${request.formGuid}/connections`, body);
  }
}
